import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileSpreadsheet } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function LoanAmortizationCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Loans", url: "https://calclab.app/loans" },
      { name: "Loan Amortization Calculator", url: "https://calclab.app/calculator/loan-amortization" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Loan Amortization Calculator",
      "Generate complete payment schedule for loans. Export to CSV or Google Sheets. Free financial planning tool.",
      "https://calclab.app/calculator/loan-amortization",
      lang
    )
  ]);
  const [loanAmount, setLoanAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [months, setMonths] = useState("");
  const [schedule, setSchedule] = useState<Array<{ month: number; payment: number; principal: number; interest: number; balance: number }>>([]);

  const calculate = () => {
    const P = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate);
    const n = parseInt(months);

    if (isNaN(P) || isNaN(annualRate) || isNaN(n) || P <= 0 || annualRate <= 0 || n <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const monthlyRate = annualRate / 100 / 12;
    const monthlyPayment = P * (monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    
    let balance = P;
    const newSchedule = [];

    for (let i = 1; i <= n; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      newSchedule.push({
        month: i,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
    }

    setSchedule(newSchedule);
    toast.success("Amortization schedule calculated!");
  };

  const exportToCSV = () => {
    if (schedule.length === 0) {
      toast.error("No schedule to export");
      return;
    }

    const headers = ["Month", "Payment", "Principal", "Interest", "Balance"];
    const rows = schedule.map(row => [
      row.month,
      row.payment.toFixed(2),
      row.principal.toFixed(2),
      row.interest.toFixed(2),
      row.balance.toFixed(2)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `loan_amortization_schedule.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("CSV exported successfully!");
  };

  const exportToGoogleSheets = () => {
    if (schedule.length === 0) {
      toast.error("No schedule to export");
      return;
    }

    const headers = ["Month", "Payment", "Principal", "Interest", "Balance"];
    const rows = schedule.map(row => [
      row.month,
      row.payment.toFixed(2),
      row.principal.toFixed(2),
      row.interest.toFixed(2),
      row.balance.toFixed(2)
    ]);

    const data = [headers, ...rows];
    const csvContent = data.map(row => row.join(",")).join("\n");
    const encodedData = encodeURIComponent(csvContent);
    
    window.open(`https://docs.google.com/spreadsheets/u/0/create?usp=sheets_web#gid=0`, "_blank");
    toast.success("Opening Google Sheets - paste the data there!");
    
    navigator.clipboard.writeText(csvContent);
    toast.success("Schedule copied to clipboard!");
  };

  const reset = () => {
    setLoanAmount("");
    setInterestRate("");
    setMonths("");
    setSchedule([]);
  };

  return (
    <PageLayout 
      calculatorName="Loan Amortization Calculator"
      calculatorPath="/calculator/loan-amortization"
      category="finance"
      subcategory="Loans"
    >
      <Card>
        <CardHeader>
          <CardTitle>Loan Amortization Calculator</CardTitle>
          <CardDescription>
            Generate complete payment schedule for loan brokers and financial planning
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="loanAmount">Loan Amount ($)</Label>
            <Input
              id="loanAmount"
              type="number"
              placeholder="50000"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="interestRate">Annual Interest Rate (%)</Label>
            <Input
              id="interestRate"
              type="number"
              placeholder="5.5"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="months">Loan Term (months)</Label>
            <Input
              id="months"
              type="number"
              placeholder="60"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {schedule.length > 0 && (
            <>
              <CalculatorSuperCalcCTA config={calculatorCTAs["loan-amortization"]} trigger={schedule.length} />
              
              <div className="flex gap-2 mt-4">
                <Button onClick={exportToCSV} variant="outline" className="gap-2">
                  <Download className="h-4 w-4" />
                  Export CSV
                </Button>
                <Button onClick={exportToGoogleSheets} variant="outline" className="gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Copy for Google Sheets
                </Button>
              </div>
              <div className="mt-4">
                <ScrollArea className="h-[500px] rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Principal</TableHead>
                        <TableHead>Interest</TableHead>
                        <TableHead>Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schedule.map((row) => (
                        <TableRow key={row.month}>
                          <TableCell>{row.month}</TableCell>
                          <TableCell>${row.payment.toFixed(2)}</TableCell>
                          <TableCell>${row.principal.toFixed(2)}</TableCell>
                          <TableCell>${row.interest.toFixed(2)}</TableCell>
                          <TableCell>${row.balance.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
